import {
  DocumentTemplateType,
  TemplateDto,
  TemplateListDto,
  DataBlockTypeDto,
  QueryFactory,
  useEnabledDataBlockTypes,
  DataBlockVariant,
  MergeFieldDto,
} from '@lmt/shared-types';
import { useEventCallback } from '@mui/material';

import { assertNever } from '@lmt/unicorn-shared/app/helpers/assert-never';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { EventArgs } from 'devexpress-richedit';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import {
  CustomCommandExecutedEventArgs,
  DXR,
  Ribbon,
  RibbonSelectBoxItem,
  RichEdit,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import { DocumentViewerMode } from '../types';
import { convertFields } from './ConvertFieldsToDocumentVariables';
import {
  showFieldCodes,
  showFieldCodesButtonId,
} from './ShowCodesAndValuesSwitcher';
import { useTabId } from '../../keep-alive/TabContext';
import {
  getDocumentTemplateFromState,
  removeDocumentTemplateFromState,
  saveDocumentTemplateToState,
} from '@lmt/unicorn-shared/app/features/unsaved-changes/operations';
import {
  addLogoutHandler,
  useClaims,
} from '@lmt/unicorn-shared/app/features/auth/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';

export const openTemplateMenuId = 'openTemplateMenuButton';

function getOpenTemplateMenu(ribbon: Ribbon) {
  const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
  const openTemplateMenu = filePanel.getItem(
    openTemplateMenuId,
  ) as RibbonSelectBoxItem;
  return openTemplateMenu;
}

function setOpenTemplateValue(richEdit: RichEdit, value: number) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const ribbon = (richEdit._native as any)!.lastRibbonSettings;
  const openTemplateMenu = getOpenTemplateMenu(ribbon);
  if (!openTemplateMenu) return;
  openTemplateMenu.value = value;
}

function createOpenTemplateMenuItem(ribbon: Ribbon, items: TemplateListDto[]) {
  const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;

  const openTemplateMenu = getOpenTemplateMenu(ribbon);
  if (openTemplateMenu) {
    openTemplateMenu.dataSource = items;
  } else {
    filePanel.insertItem(
      new DXR.RibbonSelectBoxItem(openTemplateMenuId, items, {
        showClearButton: false,
        placeholder: i18next.t('translation:documents.option_open_placeholder'),
        displayExpr: 'title',
        valueExpr: 'id',
        width: 250,
        acceptCustomValue: false,
      }),
      1,
    );
  }
}

type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  templateType: DocumentTemplateType;
  onTemplateOpened?: (templateId: TemplateDto | null) => void;
  templateId?: number | null;
  mode?: DocumentViewerMode;
  patientId?: number;
  mergeFields: MergeFieldDto[];
};

export function useGetTemplateQuery(
  templateType: DocumentTemplateType,
  mode?: DocumentViewerMode,
) {
  const dataBlockTypes = useEnabledDataBlockTypes();
  switch (templateType) {
    case DocumentTemplateType.Document:
    case DocumentTemplateType.DocumentHeader:
    case DocumentTemplateType.DocumentFooter:
      return QueryFactory.DocumentTemplateQuery.useGetQuery({
        select: (data) =>
          data
            ?.filter((x) =>
              mode === DocumentViewerMode.Designer
                ? x.type === templateType
                : x.isActive && x.type === DocumentTemplateType.Document,
            )
            .map((x) => ({
              ...x,
              title: x.isDefault ? i18next.t(x.title as any) : x.title,
            }))
            .sort((a, b) => a.title.localeCompare(b.title)),
      });

    case DocumentTemplateType.HL7OutgoingMessage:
      return QueryFactory.HL7OutgoingTemplatesQuery.useGetQuery({
        select: (data) =>
          data
            ?.map((x) => ({
              ...x,
              title: x.isDefault
                ? i18next.t(x.title as any) +
                  getTemplateTitlePostFix(dataBlockTypes, x.dataBlockTypeId)
                : x.title +
                  getTemplateTitlePostFix(dataBlockTypes, x.dataBlockTypeId),
            }))
            .sort((a, b) => a.title.localeCompare(b.title)),
      });
    case DocumentTemplateType.GDTOutgoingMessage:
      return QueryFactory.GDTOutgoingTemplatesQuery.useGetQuery({
        select: (data) =>
          data
            ?.map((x) => ({
              ...x,
              title: x.isDefault
                ? i18next.t(x.title as any) +
                  getTemplateTitlePostFix(dataBlockTypes, x.dataBlockTypeId)
                : x.title +
                  getTemplateTitlePostFix(dataBlockTypes, x.dataBlockTypeId),
            }))
            .sort((a, b) => a.title.localeCompare(b.title)),
      });
    default:
      assertNever(templateType);
  }
}

export function getTemplateTitlePostFix(
  dataBlockTypes: DataBlockTypeDto[],
  dataBlockTypeId?: number | null,
) {
  if (!dataBlockTypeId) {
    return '';
  } else {
    const type = dataBlockTypes.find((t) => t.id === dataBlockTypeId);
    if (type?.variant === DataBlockVariant.Case) {
      return i18next.t('documents.template_postfix.case');
    } else if (type?.variant === DataBlockVariant.Visit) {
      return i18next.t('documents.template_postfix.visit');
    } else {
      return '';
    }
  }
}

/*
  Adds "Open" menu with a list of available Document Templates to RichTextEditor
 */
export const OpenTemplatesRichEditMenuItem: React.FC<Props> = (props) => {
  const { richEdit } = props;
  const dispatch = useUniDispatch();
  const tabId = useTabId();
  const modals = useModal();
  const documentTemplates = useGetTemplateQuery(props.templateType, props.mode);
  const [isLoading, setLoading] = useState<boolean>(false);
  const claims = useClaims();

  const { data: patient } = QueryFactory.PatientQuery.useGetPatientQuery(
    props.patientId!,
    {
      enabled: !!props.patientId,
    },
  );

  const { data: documentTemplateRules } =
    QueryFactory.DocumentTemplateRuleQuery.useGetQuery(
      {
        subtenantId: patient?.subtenantId,
      },
      {
        enabled: !!patient?.subtenantId,
      },
    );

  useEffect(
    function OpenTemplateFromUrl() {
      // If isLoading then we call openTemplate in another function
      // when we select template from the menu
      if (!isLoading) {
        if (props.templateId) {
          openTemplate(props.templateId, richEdit.current, true).catch((e) =>
            console.error('openTemplate', e),
          );
        } else {
          richEdit?.current?.updateRibbon((ribbon) => {
            if (!ribbon) return;
            const templateMenu = getOpenTemplateMenu(ribbon);
            if (templateMenu) {
              templateMenu.value = undefined;
            }
            restoreContentFromStateForNewTemplate(richEdit.current).catch((e) =>
              console.error('openTemplate', e),
            );
          });
        }
      }
    },
    [props.templateId],
  );

  const restoreContentFromStateForNewTemplate = useEventCallback(
    async (sender: RichEdit) => {
      if (!sender || !tabId || props.mode !== DocumentViewerMode.Designer) {
        return;
      }
      if (!claims.permissions.documentTemplateManagement) {
        return;
      }
      const unsavedChangesFromState = getDocumentTemplateFromState(tabId, null);
      if (!unsavedChangesFromState) {
        return;
      }

      sender.loadingPanel.show();
      sender.beginUpdate();
      try {
        sender.openDocument(unsavedChangesFromState.content);
        const documentLoadedHandler = (
          loadedRichEdit: RichEdit | undefined,
        ) => {
          requestAnimationFrame(() => {
            richEdit.current.hasUnsavedChanges = true;
            setTabHasUnsavedChanges(true);
            removeDocumentTemplateFromState(tabId, dispatch);
            showFieldCodes(sender, props.mergeFields);
          });

          loadedRichEdit!.events.customCommandExecuted._fireEvent(
            loadedRichEdit!,
            new DXR.CustomCommandExecutedEventArgs(
              showFieldCodesButtonId,
              null,
            ),
          );
          sender.events.documentLoaded.removeHandler(documentLoadedHandler);
        };
        sender.events.documentLoaded.addHandler(documentLoadedHandler);
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        sender.loadingPanel.hide();
        sender.endUpdate();
      }
    },
  );

  useEffect(
    function FillMenuWithLoadedTemplates() {
      if (documentTemplates.isLoading) {
        // Currently we don't change anything during Loading
        // because it causes flickering
        // return createOpenTemplateMenuItem(ribbon, [
        //   new RibbonSubMenuItem('tempLoadingItem', i18next.t('Loading')),
        // ]);
      } else if (
        documentTemplates.data &&
        (props.mode === DocumentViewerMode.Designer || documentTemplateRules)
      ) {
        let templates = documentTemplates.data;
        if (documentTemplateRules) {
          templates = templates.filter((template) =>
            documentTemplateRules.some(
              (rule) => rule.templateId === template.id && rule.isActive,
            ),
          );
        }
        richEdit?.current?.updateRibbon((ribbon) => {
          if (!ribbon) return;
          createOpenTemplateMenuItem(ribbon, templates);
          getOpenTemplateMenu(ribbon).value = props.templateId;
        });
      } else if (documentTemplates.isError) {
        richEdit?.current?.updateRibbon((ribbon) => {
          if (!ribbon) return;
          createOpenTemplateMenuItem(ribbon, [
            {
              id: -1,
              title: convertToErrorString(documentTemplates.error),
              isActive: false,
              isDefault: false,
              type: DocumentTemplateType.Document,
              dataBlockTypeId: null,
            },
          ]);
        });
      }
    },
    [documentTemplates.data, documentTemplateRules],
  );
  const openTemplate = useEventCallback(
    async (documentTemplateId: number, sender: RichEdit, fromUrl?: boolean) => {
      if (richEdit?.current?.hasUnsavedChanges) {
        const isConfirmed = await modals.showConfirm({
          title: i18next.t('documents.unsaved_changes_confirmation.title'),
          text: i18next.t('documents.unsaved_changes_confirmation.text'),
          okButtonText: i18next.t(
            'documents.unsaved_changes_confirmation.ok_text',
          ),
          cancelButtonText: i18next.t(
            'documents.unsaved_changes_confirmation.cancel_text',
          ),
        });
        if (!isConfirmed) {
          richEdit?.current?.updateRibbon((ribbon) => {
            if (!ribbon) return;
            const templateMenu = getOpenTemplateMenu(ribbon);
            if (templateMenu) {
              templateMenu.value = props.templateId;
            }
          });
          return;
        }
      }
      setOpenTemplateValue(sender, documentTemplateId);
      sender.loadingPanel.show();
      sender.beginUpdate();
      try {
        const includeHeader =
          props.templateType === DocumentTemplateType.Document &&
          props.mode === DocumentViewerMode.Viewer;
        const template = await QueryFactory.DocumentTemplateClient.getSingle(
          documentTemplateId,
          includeHeader,
          props.patientId,
        );
        props.onTemplateOpened?.(template);
        const title = template.isDefault
          ? i18next.t(template.title as any)
          : template.title;

        let content = template.content;
        let hasUnsavedChanges = false;
        if (
          fromUrl &&
          props.mode === DocumentViewerMode.Designer &&
          tabId &&
          claims.permissions.documentTemplateManagement
        ) {
          const unsavedChangesFromState = getDocumentTemplateFromState(
            tabId,
            documentTemplateId,
          );
          if (unsavedChangesFromState) {
            content = unsavedChangesFromState.content;
            hasUnsavedChanges = true;
            setTabHasUnsavedChanges(true);
            removeDocumentTemplateFromState(tabId, dispatch);
          }
        }

        sender.openDocument(content, title);

        const documentLoadedHandler = (
          loadedRichEdit: RichEdit | undefined,
        ) => {
          if (props.templateType === DocumentTemplateType.GDTOutgoingMessage) {
            convertFields(sender);
          }

          // we don't need to do it for Designer (#46557: "Save" button is active after clicking "Save as" in Document templates)
          if (props.mode === DocumentViewerMode.Viewer) {
            // Fixes #37634: Document based on a template without autocomplete fields is not saved
            setTimeout(() => {
              if (!richEdit.current.isDisposed) {
                richEdit.current.hasUnsavedChanges = true;
                richEdit.current.events.documentChanged._fireEvent(
                  richEdit.current,
                  new EventArgs(),
                );
              }
            }, 1000);
          } else {
            setTimeout(() => {
              richEdit.current.hasUnsavedChanges = hasUnsavedChanges;
            }, 1000);
          }

          loadedRichEdit!.events.customCommandExecuted._fireEvent(
            loadedRichEdit!,
            new DXR.CustomCommandExecutedEventArgs(
              showFieldCodesButtonId,
              null,
            ),
          );
          sender.events.documentLoaded.removeHandler(documentLoadedHandler);
        };
        sender.events.documentLoaded.addHandler(documentLoadedHandler);
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        sender.loadingPanel.hide();
        sender.endUpdate();
        setLoading(false);
      }
    },
  );

  useEffect(function AddSubmenuClickHandler() {
    const richEditCurrent = richEdit.current;

    const customCommandExecutedEventHandler = async (
      sender: RichEdit | undefined,
      args: CustomCommandExecutedEventArgs | undefined,
    ) => {
      if (!sender || !args) {
        return;
      }
      if (args.commandName == openTemplateMenuId) {
        const documentTemplateId = args.parameter as number;
        setLoading(true);
        await openTemplate(documentTemplateId, sender);
      }
    };

    richEditCurrent.events.customCommandExecuted.addHandler(
      customCommandExecutedEventHandler,
    );

    return () => {
      richEditCurrent.events.customCommandExecuted.removeHandler(
        customCommandExecutedEventHandler,
      );
    };
  }, []);

  const setTabHasUnsavedChanges = useEventCallback((hasChanges: boolean) => {
    if (tabId) {
      dispatch(
        NavigationActions.tabHasUnsavedChangesChanged({
          id: tabId,
          hasUnsavedChanges: hasChanges,
        }),
      );
    }
  });

  useEffect(() => {
    return addLogoutHandler(() => {
      if (props.mode === DocumentViewerMode.Designer) {
        if (tabId && richEdit.current.hasUnsavedChanges) {
          richEdit.current.exportToBase64((base64: string) => {
            saveDocumentTemplateToState(
              tabId,
              props.templateId ?? null,
              base64,
              dispatch,
            );
          });
        }
      }
    });
  }, [props.templateId]);

  return null;
};
